import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { InvoiceLineItemValidation } from 'src/redux/billing/billing.types';
import { usePatchInvoiceLineItemValidationMutation } from 'src/redux/billing/billing.endpoints';
import {
  fieldLabel,
  getDisplayableLineItemValidations,
} from './line-item-validation-details-content';
import {
  mapInvoiceLineItemValidationFieldToLineItemKey,
  parseValidationValueForLineItemField,
} from '../../utils/billing-utils';

export interface LineItemValidationInvalidSummaryProps {
  validations: InvoiceLineItemValidation[];
  /** When all are set, Apply / Ignore actions are shown (invoice line drawer). */
  lineItemId?: string;
  invoiceGroupId?: string;
  isLocked?: boolean;
  updateLineItem?: (lineItemId: string, field: string, value: unknown) => Promise<void>;
  refetchInvoiceGroup?: () => void | Promise<void>;
}

/**
 * Compact list: field + proposed value, with optional Apply / Ignore per row.
 */
const LineItemValidationInvalidSummary: React.FC<LineItemValidationInvalidSummaryProps> = ({
  validations,
  lineItemId,
  invoiceGroupId,
  isLocked = false,
  updateLineItem,
  refetchInvoiceGroup,
}) => {
  const [patchValidation] = usePatchInvoiceLineItemValidationMutation();
  const [pending, setPending] = useState<{ id: string; action: 'apply' | 'ignore' } | null>(null);

  const rows = getDisplayableLineItemValidations(validations);
  const showActions =
    lineItemId != null &&
    invoiceGroupId != null &&
    invoiceGroupId !== '' &&
    typeof updateLineItem === 'function' &&
    typeof refetchInvoiceGroup === 'function';

  if (rows.length === 0) {
    return (
      <p className="text-xs text-gray-600">
        No validation details available.
      </p>
    );
  }

  const runAfterPatch = async() => {
    await refetchInvoiceGroup?.();
  };

  const handleApply = async(v: InvoiceLineItemValidation) => {
    if (!showActions || isLocked || pending) {
      return;
    }
    const fieldKey = mapInvoiceLineItemValidationFieldToLineItemKey(v.field);
    if (fieldKey == null) {
      return;
    }
    const parsed = parseValidationValueForLineItemField(fieldKey, v.value);
    if (parsed == null) {
      return;
    }
    setPending({ id: v.id, action: 'apply' });
    try {
      await updateLineItem!(lineItemId!, fieldKey, parsed);
      await patchValidation({
        id: v.id,
        data: { applied: true },
        invoiceGroupId,
      }).unwrap();
      await runAfterPatch();
    } finally {
      setPending(null);
    }
  };

  const handleIgnore = async(v: InvoiceLineItemValidation) => {
    if (!showActions || isLocked || pending) {
      return;
    }
    setPending({ id: v.id, action: 'ignore' });
    try {
      await patchValidation({
        id: v.id,
        data: { status: 'archived' },
        invoiceGroupId,
      }).unwrap();
      await runAfterPatch();
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-900">
        Incorrect values
      </p>
      <ul className="space-y-2">
        {rows.map((v) => {
          const fieldKey = mapInvoiceLineItemValidationFieldToLineItemKey(v.field);
          const parsed =
            fieldKey != null ? parseValidationValueForLineItemField(fieldKey, v.value) : null;
          const canApply = showActions && !isLocked && fieldKey != null && parsed != null && !pending;
          const rowBusy = pending?.id === v.id;
          const applyLabel =
            rowBusy && pending?.action === 'apply' ? 'Applying…' : 'Apply';
          const ignoreLabel =
            rowBusy && pending?.action === 'ignore' ? 'Ignoring…' : 'Ignore';

          return (
            <li
              key={v.id}
              className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5"
            >
              <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                Field
              </div>
              <div className="text-sm font-medium text-gray-900">
                {fieldLabel(v.field)}
              </div>
              <div className="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                Proposed value
              </div>
              <div className="break-all font-mono text-sm text-gray-900">
                {v.value != null && v.value !== '' ? v.value : '—'}
              </div>
              {showActions && (
                <div className="mt-2 flex w-full flex-wrap items-center justify-between gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={isLocked || rowBusy || Boolean(pending)}
                    onClick={() => handleIgnore(v)}
                  >
                    {ignoreLabel}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="black"
                    className="h-7 text-xs text-white hover:bg-gray-900"
                    disabled={!canApply || rowBusy}
                    onClick={() => handleApply(v)}
                  >
                    {applyLabel}
                  </Button>
                </div>
              )}
              {showActions && fieldKey == null && (
                <p className="mt-1 text-[10px] text-amber-800">
                  This field cannot be applied automatically.
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default LineItemValidationInvalidSummary;
